import React from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  EmptyState, ListTable, Panel, SearchField, StatusChip, ToolbarButton, formatRelativeTime, type ListColumn,
} from '../../ui-kit';
import { ConfirmDialog } from '../../components/dialog';
import { listSharedViewers, removeShareViewer, type SharedViewerRow } from '../../services/sharingService';
import { openedWords } from '../sharing/ShareDialog';

const LINK_STATE: Record<SharedViewerRow['share_state'], { tone: 'good' | 'neutral'; label: string }> = {
  live: { tone: 'good', label: 'Link on' },
  expired: { tone: 'neutral', label: 'Link expired' },
  revoked: { tone: 'neutral', label: 'Link turned off' },
};

/**
 * Everyone something in the workspace is shared with: named viewers, not
 * members. One row per person per link, with when they last opened it, and
 * Remove, which cuts them off on their next click.
 */
export default function SharedViewersTab() {
  const [rows, setRows] = React.useState<SharedViewerRow[] | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [removing, setRemoving] = React.useState<SharedViewerRow | null>(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    listSharedViewers()
      .then((list) => { setRows(list); setProblem(null); })
      .catch((err: Error) => setProblem(err.message));
  }, []);
  React.useEffect(load, [load]);

  const shown = (rows ?? []).filter((r) => !r.removed).filter((r) => {
    const q = query.trim().toLowerCase();
    return !q || [r.email, r.name, r.target_name].some((v) => (v ?? '').toLowerCase().includes(q));
  });

  const columns: ListColumn<SharedViewerRow>[] = [
    {
      key: 'person', header: 'Person', grow: 1.2, minWidth: 200, sortValue: (r) => r.name || r.email,
      render: (r) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            {r.name || r.email}
          </Typography>
          {r.name || r.workspace_user ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
              {r.name ? r.email : 'Also a member of this workspace'}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'shared', header: 'Shared with them', grow: 1, minWidth: 180, sortValue: (r) => r.target_name,
      render: (r) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{r.target_name ?? 'Something no longer there'}</Typography>
          <StatusChip {...LINK_STATE[r.share_state]} plain />
        </Stack>
      ),
    },
    {
      key: 'opened', header: 'Opened', width: 140, sortValue: (r) => r.times_opened, sortStartsDescending: true,
      render: (r) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: r.times_opened ? t.tokens.text.primary : t.tokens.text.tertiary })}>
            {openedWords(r.times_opened)}
          </Typography>
          {r.times_opened && r.last_seen_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              last {formatRelativeTime(r.last_seen_at)}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'actions', header: 'Actions', width: 80, align: 'right',
      render: (r) => (
        <ToolbarButton tone="danger" icon={<DeleteOutlineRounded fontSize="small" />}
          aria-label={`Remove ${r.name || r.email}`} tooltip="Remove" onClick={() => setRemoving(r)} />
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {problem ? <Alert severity="error">{problem}</Alert> : null}
      <Panel
        title="Viewers"
        subtitle="People outside the workspace who can open something shared with them. Each verified their email with a code. They see only what was shared, read-only."
        search={<SearchField value={query} onChange={setQuery} placeholder="Search people or boards…" />}
      >
        {rows && rows.length === 0 ? (
          <EmptyState message="Nothing is shared yet. Use Share on a board to send someone a read-only link." />
        ) : (
          <ListTable columns={columns} rows={shown} getRowKey={(r) => r.viewer_id} loading={rows === null}
            pageSize={10} pageSizeOptions={[10, 25, 50]} emptyMessage="Nobody matches. Clear the search." />
        )}
      </Panel>
      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name || removing?.email || 'them'}?`}
        description={`They lose access to ${removing?.target_name ?? 'it'} on their next click. They can be added again from the board's Share window.`}
        tone="destructive"
        confirmLabel="Remove"
        busy={busy}
        onConfirm={async () => {
          if (!removing) return;
          setBusy(true);
          try {
            await removeShareViewer(removing.share_id, removing.viewer_id);
            setRemoving(null);
            load();
          } catch (err) {
            setProblem(err instanceof Error ? err.message : 'Could not remove them');
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}
